import React, { useEffect, useState, useMemo } from 'react';
import { DonutChart } from '@/features/journal/components/charts/DonutChart';
import { get } from '@/features/shared/lib/api';
import { ChartSkeletonLoader } from '@/features/shared/ui/ChartSkeletonLoader';

export function TopCategoriesByDB({ data: externalData = null, deptId = null, mode = null }) {
  // Define color palettes first
  const palettes = {
    WOS: ['#5B3CC4', '#8C6BE3', '#B298F2', '#D7C9FF', '#E4E8F1'],
    Scopus: ['#F26B2A', '#FFA56E', '#FFBE8D', '#FFD7B6', '#E4E8F1'],
    KCI: ['#0B5EA8', '#2A7BC5', '#5FA3E0', '#A8CCF0', '#E4E8F1'],
  };

  // API에서 데이터 가져오기 (externalData가 없을 때만)
  const [categoriesData, setCategoriesData] = useState(null);
  const [categoriesLoading, setCategoriesLoading] = useState(externalData === null);
  const [categoriesErr, setCategoriesErr] = useState(null);

  useEffect(() => {
    // externalData가 제공되면 API 호출하지 않음
    if (externalData !== null) {
      return;
    }

    // deptId가 명시적으로 필요한 상황(예: 학과 상세 페이지)에서 deptId가 없으면 대기
    // Journals 페이지 같은 곳에서는 deptId 없이 호출할 수 있으므로, 
    // 호출하는 쪽의 의도에 따라 다를 수 있음.
    // 하지만 <TopCategoriesByDB /> 가 학과 상세에서 record?.category_id를 dept_id로 전달한다고 했으므로
    // deptId가 prop으로 정의되어 있다면 값이 올 때까지 기다리는 것이 안전할 수 있음.
    // 다만 Journals.jsx 에서는 deptId 없이 사용하므로, 
    // "deptId가 prop으로 넘어왔는데 값이 null/undefined인 경우"만 체크함.

    // 이 컴포넌트가 학과 상세 페이지에서 사용될 때는 deptId가 반드시 있어야 함.
    // Journals 페이지 등 공통 페이지에서는 deptId가 prop으로 전달되지 않을 것임(undefined).

    const isDepartmentPage = window.location.pathname.includes('/departments');
    if (isDepartmentPage && mode === 'detail' && !deptId) {
      return;
    }

    let alive = true;
    async function fetchCategories() {
      setCategoriesLoading(true);
      setCategoriesErr(null);
      try {
        const params = {};
        if (deptId) {
          params.dept_id = deptId;
        }
        const response = await get('/rm/v1/journal/top_categories', { params });
        if (alive) {
          if (response?.status && response?.result) {
            setCategoriesData(response.result);
          } else {
            setCategoriesErr(response?.message || '데이터를 가져오는데 실패했습니다.');
            setCategoriesData(null);
          }
        }
      } catch (e) {
        if (alive) {
          setCategoriesErr(e?.response?.data?.message || e.message);
        }
      } finally {
        if (alive) {
          setCategoriesLoading(false);
        }
      }
    }
    fetchCategories();
    return () => { alive = false; };
  }, [externalData, deptId]);

  // 데이터 처리: externalData 또는 API 데이터 사용
  const processedData = useMemo(() => {
    const data = externalData || categoriesData;
    if (!data || typeof data !== 'object') {
      return { WOS: [], Scopus: [], KCI: [] };
    }

    // Attach color to each datum so UI can reuse it consistently
    const WOS = (data.WOS || []).map((d, i) => ({ ...d, color: palettes.WOS[i] || palettes.WOS[palettes.WOS.length - 1] }));
    const SCOPUS = (data.Scopus || []).map((d, i) => ({ ...d, color: palettes.Scopus[i] || palettes.Scopus[palettes.Scopus.length - 1] }));
    const KCI = (data.KCI || []).map((d, i) => ({ ...d, color: palettes.KCI[i] || palettes.KCI[palettes.KCI.length - 1] }));

    return { WOS, Scopus: SCOPUS, KCI };
  }, [externalData, categoriesData]);

  const { WOS, Scopus: SCOPUS, KCI } = processedData;

  // 데이터가 있는 DB만 표시 (데이터 없으면 해당 영역 숨김)
  const sections = useMemo(() => {
    const list = [
      { key: 'WOS', title: 'WOS', data: WOS, colors: palettes.WOS },
      { key: 'Scopus', title: 'Scopus', data: SCOPUS, colors: palettes.Scopus },
      { key: 'KCI', title: 'KCI', data: KCI, colors: palettes.KCI },
    ];
    return list.filter((s) => s.data.length > 0);
  }, [WOS, SCOPUS, KCI]);

  const hasData = sections.length > 0;

  // 1개/2개/3개에 맞춘 그리드: 1개는 중앙 정렬, 2~3개는 균등 배치
  const gridColsClass =
    sections.length === 1
      ? 'grid-cols-1 max-w-[280px] mx-auto'
      : sections.length === 2
        ? 'grid-cols-1 md:grid-cols-2'
        : 'grid-cols-1 md:grid-cols-3';

  return (
    <div className="w-full relative min-h-[240px]">
      {/* 로딩 상태 오버레이 */}
      <ChartSkeletonLoader show={externalData === null && (categoriesLoading || categoriesData === null) && !categoriesErr} />

      {!(externalData === null && (categoriesLoading || categoriesData === null)) && hasData && (
        <>
          <div className={`grid gap-6 ${gridColsClass}`}>
            {sections.map(({ key, title, data, colors }) => (
              <div key={key} className="bg-white rounded-xl min-[800px]:bg-transparent flex flex-col items-center">
                <DonutChart title={title} data={data} colors={colors} />
              </div>
            ))}
          </div>

          <div className={`mt-6 grid gap-6 ${gridColsClass}`}>
            {sections.map(({ key, data, colors }) => (
              <div key={key} className="space-y-2 flex flex-col items-center md:items-start">
                {data.map((item, i) => {
                  const color = item?.color || colors[i] || colors[colors.length - 1];
                  const label = item?.label || '';
                  return (
                    <div key={i} className="flex items-center text-[#6C7687] text-[12px] w-full max-w-[240px]">
                      <span className="flex-shrink-0 inline-block w-[12px] h-[12px] rounded-full mr-[6px]" style={{ background: color }} />
                      <span className="truncate" title={label}>{label}</span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </>
      )}

      {/* 에러 상태 오버레이 */}
      {externalData === null && categoriesErr && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 text-[#F3652D] text-sm">
          {categoriesErr}
        </div>
      )}

      {/* 데이터 없음 오버레이 */}
      {categoriesData !== null && !hasData && !categoriesLoading && !categoriesErr && (
        <div className="absolute inset-0 flex items-center justify-center text-[#8D9AAF]">
          데이터가 없습니다.
        </div>
      )}
    </div>
  );
}

export default TopCategoriesByDB;
